
import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';


import { ProductSlideshowSort } from 'haba-contentful-components';
import { ActiveCartService, I18nModule, isNotUndefined, Product } from 'haba-core';
import {
  EmporixProduct,
  EmporixProductLabelsNormalizer,
  HabaProductService,
  mapProductForProductCardBase,
  ProductHit
} from 'haba-emporix';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  Subject,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { BREAKPOINT, BreakpointService } from 'haba-storefrontlib';
import { ButtonQaId, HabaQaId } from '@app/helpers';
import { CartApiService, MessagesService } from '@app/services';
import { DataLayerService, DlEventName } from 'haba-base-components';
import { ProductAdvisorCardComponent } from '../product-advisor-card/product-advisor-card.component';

@Component({
  selector: 'app-product-advisor-products-list',
  templateUrl: './product-advisor-products-list.component.html',
  styleUrls: ['./product-advisor-products-list.component.scss'],
  imports: [I18nModule, ProductAdvisorCardComponent],
})
export class ProductAdvisorProductsListComponent implements OnChanges, OnDestroy {
  @Input() productRecommendations: string[];

  public entries: Product[];
  public products: ProductHit[] = [];
  public totalPrice: number;
  public isDesktop: boolean;
  public addToCartDisabled: boolean = false;
  public baseQaId: HabaQaId;
  public addToCartButtonQaId: ButtonQaId;
  public noConfigurableProducts: boolean;

  private unsubscribe$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private habaProductService: HabaProductService,
    private emporixProductLabelsNormalizer: EmporixProductLabelsNormalizer,
    private breakpointService: BreakpointService,
    private activeCartService: ActiveCartService,
    private cartApiService: CartApiService,
    private dataLayerService: DataLayerService,
    private messagesService: MessagesService
  ) {}

  ngOnChanges(): void {
    this.initIds();

    if (this.productRecommendations?.length) {
      this.habaProductService
        .getSlideshowProducts(this.productRecommendations, ProductSlideshowSort.FIXED)
        .pipe(takeUntil(this.unsubscribe$), filter(isNotUndefined))
        .subscribe((products) => {
          this.entries = products;

          this.products = products
            .map((product) =>
              mapProductForProductCardBase(
                this.emporixProductLabelsNormalizer.convert(product as EmporixProduct)
              )
            )
            .filter((product) => Boolean(product));

          this.totalPrice = this.products.reduce((acc, curr) => acc + curr.price, 0);
          this.noConfigurableProducts = this.products.every((product) => !product.configurable);

          this.cdr.detectChanges();
        });
    } else {
      this.products = [];
    }

    this.breakpointService
      .isUp(BREAKPOINT.md)
      .pipe(takeUntil(this.unsubscribe$), distinctUntilChanged())
      .subscribe((isDesktop) => {
        this.isDesktop = isDesktop;
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(undefined);
    this.unsubscribe$.complete();
  }

  public addEntriesToCart(): void {
    this.activeCartService
      .getActiveCartId()
      .pipe(
        take(1),
        switchMap((cartId) => {
          const validEntries = [];
          this.entries.forEach((entry) => {
            if (entry.stock?.stockLevel < 1) {
              return;
            }

            const entryRequest: any = {
              product: {
                id: entry.id,
                siteCode: entry.siteCode,
                yrn: entry.yrn,
                name: entry.name,
                mixins: entry.mixins,
              },
              cartId: cartId,
              quantity: 1,
              price: {
                priceId: entry.price.id,
                originalAmount: entry.price.value,
                effectiveAmount: entry.price.value,
                currency: entry.price.currencyIso,
              },
            };

            validEntries.push(entryRequest);
          });

          validEntries.forEach((el) => {
            this.dataLayerService.setDataLayerFromEvent(DlEventName.ADD_TO_CART, {
              productCode: el.id,
              quantity: el.quantity,
            });
          });

          return this.cartApiService.addMultipleEntries(validEntries);
        }),
        catchError(() => {
          this.messagesService.showError('directOrderForm.messageAddedToCart');
          return EMPTY;
        })
      )
      .subscribe((result) => {
        if (result) {
          this.activeCartService.reloadActiveCart();
          this.messagesService.showSuccess('directOrderForm.messageAddedToCart');
        }
      });
  }

  private initIds() {
    this.baseQaId = this.baseQaId || HabaQaId.forPage(HabaQaId.page.productDetails);
    this.addToCartButtonQaId = this.baseQaId.getButtonId(HabaQaId.elements.button.addToCart);
  }
}
